// One-click-away methodology + known-failure-modes write-up for
// /electionday. Plain language, no jargon, no maths unless the
// editor specifically needs it. Lives inside StatsPanel as a
// collapsible <details> rather than a modal so the reader can read
// it without losing their place in the numbers above.

import {
  MIN_CLEAN_DIRECTIONAL,
  MIN_CLEAN_PROJECTED,
  MIN_DISTRICTS_COVERED,
  PROJECTED_MIN_GAP,
  TOTAL_DISTRICTS,
} from './stats';

export default function MethodologyNote() {
  return (
    <details className="electionday-stats__methodology">
      <summary>How this works · known failure modes</summary>
      <div className="electionday-stats__methodology-body">
        <h4>How a read becomes a vote</h4>
        <ol>
          <li>
            The editor records video of ballots being counted and feeds it
            to the still extractor. Frames are grabbed in the browser at
            a fixed rate (or when motion settles, if event-detection mode
            is on).
          </li>
          <li>
            For each kept still, the rectifier crops tight to the bright
            paper quadrilateral and warps it to a flat, upright ballot
            image. Cropping tight is the deliberate mitigation for
            adjacent-colour contamination — see <em>failure modes</em>{' '}
            below.
          </li>
          <li>
            The rectified crop is sent to the Anthropic vision model,
            which reports the party next to the handwritten “1” plus a
            confidence and one-line reason.
          </li>
          <li>
            A read is <strong>clean</strong> only when the model returns
            PL or PN at <em>high</em> confidence. Everything else —
            OTHER, UNREADABLE, medium / low confidence, or stills where
            the ballot couldn’t be localised — is counted as{' '}
            <strong>abstained</strong>, never silently dropped.
          </li>
        </ol>

        <h4>How the national share is computed</h4>
        <p>
          Malta’s {TOTAL_DISTRICTS} electoral districts are treated as
          equal-sized strata until real eligible-voter counts are wired
          in. Because counts within a district are randomised across
          boxes, an equal-stratum sample is unbiased for the national
          share — so the national PL share is simply the pooled fraction
          of clean PL reads across all districts.
        </p>
        <p>
          No design-effect inflation is applied. Stratified equal strata
          plus random box distribution justifies treating the pool as a
          simple random sample, and the 95% interval shown is a Wilson
          score interval on that pooled proportion.
        </p>

        <h4>Why three call states, not just one number</h4>
        <ul>
          <li>
            <strong>INSUFFICIENT — NO CALL.</strong> Fewer than{' '}
            {MIN_CLEAN_DIRECTIONAL} pooled clean reads, fewer than{' '}
            {MIN_DISTRICTS_COVERED} districts represented, or a 95%
            interval that still straddles 50/50. National headline is
            suppressed; only the per-district reads are shown.
          </li>
          <li>
            <strong>DIRECTIONAL — INDICATIVE.</strong> At least{' '}
            {MIN_CLEAN_DIRECTIONAL} pooled clean reads across at least{' '}
            {MIN_DISTRICTS_COVERED} districts AND the 95% Wilson interval
            sits entirely on one side of 50%. Treat this as a steer, not
            a call — sampling noise is still material at this size.
          </li>
          <li>
            <strong>PROJECTED CALL.</strong> ≥ {MIN_CLEAN_PROJECTED}{' '}
            pooled clean reads AND the lead is at least ~
            {Math.round(PROJECTED_MIN_GAP * 200)}pp (≈ a 9-point gap).
            Wilson interval is comfortably non-overlapping; absent
            unusual contamination this is the projection.
          </li>
        </ul>

        <h4>Known failure modes</h4>
        <ul>
          <li>
            <strong>Adjacent-colour contamination.</strong> Coloured
            objects near the ballot — face shields, rosettes, folders,
            flags — can leak into a colour-based read if the paper crop
            isn’t tight. The auto-rectifier is tuned to crop to the
            bright paper edges; when auto-confidence is low, the editor
            should drag the 4 corners manually before reading.
          </li>
          <li>
            <strong>Handwriting ambiguity.</strong> A “1” written near a
            party-strip boundary can be assigned to the wrong section.
            The model is instructed to flag this as medium / low
            confidence, which routes the read to abstained rather than
            into the tally.
          </li>
          <li>
            <strong>District coverage skew.</strong> Equal-stratum
            pooling depends on districts being sampled evenly enough.
            Heavily over-sampling one district will bias the national
            share. The “districts covered” counter shows whether the
            sample is spread.
          </li>
          <li>
            <strong>Camera angle / counter bias.</strong> If every clip
            is shot over the shoulder of one PL or PN counter, the
            sample may over-represent that party’s ballots before
            randomisation completes. Spread footage across counts and
            districts.
          </li>
          <li>
            <strong>Selection bias of stills.</strong> Fixed-rate
            extraction is uniform in time; event-detection mode favours
            visually busy moments (which may correlate with disputed
            ballots). The abstain rate is the canary — a rise above
            ~30% almost always means a method issue, not voter
            behaviour.
          </li>
          <li>
            <strong>Early-count clustering.</strong> The first ballots
            counted often come off the top of a single box, which is not
            yet a random sample within a district. Treat directional
            calls in the first 20–30 minutes of counting with extra
            scepticism.
          </li>
        </ul>
      </div>
    </details>
  );
}
